import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Callback page that runs after the user returns from Emergent's Google
// OAuth flow. The URL fragment contains #session_id=XXX; we exchange it
// with the backend exactly once (guarded by a ref so React StrictMode's
// double-mount doesn't double-charge the session), then redirect.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
// URLS, THIS BREAKS THE AUTH.
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error('Missing Google sign-in session id. Please try again.');
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/auth/google`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        setUser(data);
        // Strip the fragment from URL before navigating so a refresh
        // doesn't re-trigger the (now-consumed) session exchange.
        window.history.replaceState(null, '', '/dashboard');
        navigate('/dashboard', { replace: true, state: { user: data } });
        toast.success(`Welcome, ${data.name || data.email}!`);
      } catch (err) {
        const msg = err?.response?.data?.detail || 'Google sign-in failed.';
        toast.error(msg);
        navigate('/login', { replace: true });
      }
    })();
  }, [location, navigate, setUser]);

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--background-main)' }}
    >
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-[#8B5A3C] animate-spin mx-auto mb-3" />
        <p className="text-[var(--text-secondary)]">Signing you in…</p>
      </div>
    </div>
  );
}
